/* eslint-disable react/no-this-in-sfc */
import React, { useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { Form, Formik, ErrorMessage } from 'formik';
import { SubmitButton, MultiSelectField, InputField } from '@nintendo/forms';
import moment from 'moment-timezone';
import { DATE_ONLY_FORMAT, SERVER_TIMEZONE } from '../../../utils/Constants';
import DateSearchField from '../../common/DateSearchField';
import { SearchObjectType } from '../../common/datatables/DataGridControlled';

const ACTUALS_TRAANSACTION_SOURCES = [
    'AP ERV',
    'AP Expense',
    'AP INVOICE',
    'AP NRTax',
    'AP Variance',
    'Inventory',
    'Inventory Misc',
    'NIN ChRM Claims',
    'NIN FM Legacy Costs',
    'NIN Inventory',
    'NIN Packout Use Tax',
    'NIN Payables',
    'NIN Project Adjustments',
    'NIN Sales Orders',
    'NIN Time and Labor',
    'NIN Vertex',
    'Oracle Time and Labor',
];

interface ActualsFilterProps {
    setSearchObject: Function;
    searchString: string;
    searching: boolean;
    onSubmit: () => void;
}

export interface ActualsSearchFormType {
    dateFrom: string | undefined;
    dateTo: string | undefined;
    transactionSources: string[];
    textSearchValues: string;
}

const ActualsFilter = ({
    setSearchObject,
    searchString,
    searching,
    onSubmit,
}: ActualsFilterProps) => {
    const searchObjectInitial = JSON.parse(searchString);

    const [refresh, setRefresh] = useState(0);

    const transactionSourceTypes = ACTUALS_TRAANSACTION_SOURCES.map((source) => ({
        value: source,
        label: source,
    }));

    const initialValues: ActualsSearchFormType = {
        dateFrom: searchObjectInitial.dateFrom,
        dateTo: searchObjectInitial.dateTo,
        transactionSources: searchObjectInitial.transactionSources
            ? searchObjectInitial.transactionSources
            : [],
        textSearchValues: searchObjectInitial.textSearchValues || '',
    };

    const ActualssValidationSchema = Yup.object().shape({
        dateFrom: Yup.date()
            .nullable()
            .typeError('From Date is invalid')
            .test(
                'date-from-to-check',
                'Date From must be before or equal to Date To',
                function test(dateFrom) {
                    if (!dateFrom || !this.parent.dateTo) {
                        return true;
                    }
                    const fromMoment = moment(dateFrom);
                    const toMoment = moment(this.parent.dateTo);
                    return fromMoment.isSameOrBefore(toMoment);
                },
            ),
        dateTo: Yup.date().nullable().typeError('To Date is invalid'),
    });

    return (
        <Container fluid className="border-top pt-3">
            <Formik
                initialValues={initialValues}
                validationSchema={ActualssValidationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    const searchObject: SearchObjectType = {
                        dateFrom: values.dateFrom ? new Date(values.dateFrom).toUTCString() : null,
                        dateTo: values.dateTo ? new Date(values.dateTo).toUTCString() : null,
                        transactionSources:
                            values.transactionSources.length > 0 ? values.transactionSources : null,
                        textSearchValues: values.textSearchValues,
                        refresh,
                    };

                    setSearchObject(searchObject);
                    setRefresh(refresh + 1);
                    setSubmitting(false);
                    onSubmit();
                }}
                render={({ values, touched, errors, setValues, isSubmitting }) => (
                    <Form>
                        <Container fluid>
                            <Row>
                                <Col>
                                    <Row>
                                        <Col>
                                            <div
                                                className={`form-group row ${
                                                    touched.dateFrom &&
                                                    errors.dateFrom &&
                                                    'has-error'
                                                } ${
                                                    touched.dateTo && errors.dateTo && 'has-error'
                                                }`}
                                            >
                                                <div className="col-sm-3 control-label breakdown-label">
                                                    Date
                                                </div>
                                                <div className="col-sm-9">
                                                    <div
                                                        className={`form-group row mb-0 ${
                                                            touched.dateFrom &&
                                                            errors.dateFrom &&
                                                            'has-error'
                                                        } ${
                                                            touched.dateTo &&
                                                            errors.dateTo &&
                                                            'has-error'
                                                        }`}
                                                    >
                                                        <DateSearchField
                                                            label="Date From"
                                                            name="dateFrom"
                                                            flatpickrProps={{
                                                                options: {
                                                                    altInputClass: 'form-control',
                                                                    altFormat: DATE_ONLY_FORMAT,
                                                                    enableTime: false,
                                                                    dateFormat: DATE_ONLY_FORMAT,
                                                                    defaultDate: '',
                                                                },
                                                                value: values.dateFrom
                                                                    ? moment(
                                                                          values.dateFrom,
                                                                      ).format(DATE_ONLY_FORMAT)
                                                                    : undefined,
                                                            }}
                                                            timezone={SERVER_TIMEZONE}
                                                            touched={touched.dateFrom}
                                                            errors={errors.dateFrom}
                                                            contentCssClass="col-sm-6"
                                                            placeholder="from"
                                                            testId="dateFrom"
                                                        />
                                                        <DateSearchField
                                                            label="Date To"
                                                            name="dateTo"
                                                            flatpickrProps={{
                                                                options: {
                                                                    altInputClass: 'form-control',
                                                                    altFormat: DATE_ONLY_FORMAT,
                                                                    enableTime: false,
                                                                    dateFormat: DATE_ONLY_FORMAT,
                                                                    defaultDate: '',
                                                                },
                                                                value: values.dateTo
                                                                    ? moment(values.dateTo).format(
                                                                          DATE_ONLY_FORMAT,
                                                                      )
                                                                    : undefined,
                                                            }}
                                                            timezone={SERVER_TIMEZONE}
                                                            touched={touched.dateTo}
                                                            errors={errors.dateTo}
                                                            contentCssClass="col-sm-6"
                                                            placeholder="to"
                                                            testId="dateTo"
                                                        />
                                                    </div>

                                                    <ErrorMessage
                                                        name="dateFrom"
                                                        className="text-danger"
                                                        component="small"
                                                    />
                                                    <ErrorMessage
                                                        name="dateTo"
                                                        className="text-danger"
                                                        component="small"
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col>
                                            <MultiSelectField
                                                selectProps={
                                                    {
                                                        closeMenuOnSelect: false,
                                                    } as any
                                                }
                                                selectedOptions={
                                                    values.transactionSources
                                                        ? transactionSourceTypes
                                                              .filter((sourceOption) =>
                                                                  values.transactionSources.includes(
                                                                      sourceOption.value,
                                                                  ),
                                                              )
                                                              .map((sourceOption) => ({
                                                                  label: sourceOption.label,
                                                                  value: sourceOption.value,
                                                              }))
                                                        : []
                                                }
                                                options={transactionSourceTypes}
                                                label="Source"
                                                name="transactionSources"
                                                touched={touched.transactionSources}
                                                errors={errors.transactionSources}
                                                labelCssClass="col-sm-4"
                                                contentCssClass="col-sm-8"
                                                testId="transactionSources"
                                            />
                                        </Col>
                                        <Col>
                                            <InputField
                                                label="Keywords"
                                                name="textSearchValues"
                                                touched={touched.textSearchValues}
                                                errors={errors.textSearchValues}
                                                labelCssClass="col-sm-4"
                                                contentCssClass="col-sm-8"
                                                testId="textSearchValues"
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col
                                    xs="1"
                                    className="align-self-center align-self-middle text-nowrap mb-3"
                                >
                                    <SubmitButton
                                        className="btn-primary"
                                        disabled={searching}
                                        isSubmitting={isSubmitting}
                                        buttonText={<i className="fas fa-search-plus" />}
                                    />
                                    <Button
                                        variant="outline-primary"
                                        className="ml-1"
                                        onClick={() => {
                                            setValues({
                                                dateFrom: undefined,
                                                dateTo: undefined,
                                                transactionSources: [],
                                                textSearchValues: '',
                                            });
                                        }}
                                    >
                                        <i className="fas fa-backspace" />
                                    </Button>
                                </Col>
                            </Row>
                        </Container>
                    </Form>
                )}
            />
        </Container>
    );
};

export default ActualsFilter;
